import React from "react";
import { useParams } from "react-router-dom";
import { useCredential } from "../../hooks/useCredential";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import { getTranslation } from "../../i18n/translations";
import ErrorPage from "../ErrorPage/ErrorPage";
import "./CredentialPage.css";

const CredentialPage = () => {
  const { badgeId } = useParams();
  const { credential, loading, error } = useCredential(badgeId);

  const lang = credential?.languageCode || "es";
  const t = getTranslation(lang).credentialPage;

  const documentTitle = credential
    ? t.documentTitle.replace("{name}", credential.customerName)
    : null;
  useDocumentTitle(documentTitle);

  if (loading) {
    return (
      <div className="credential-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error || !credential) {
    return <ErrorPage lang={lang} />;
  }

  const formatDate = (dateString) => {
    if (!dateString) return t.permanent;
    const [year, month, day] = dateString.split("-");
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="credential-container">
      <div className="credential-card">
        {/* Left Panel */}
        <div className="credential-left-panel">
          <div className="credential-header-info">
            <h2 className="consultant-name">{credential.customerName}</h2>
            <p className="company-name">Immunotec</p>
            <p className="certification-title">{credential.title}</p>
          </div>
          <div className="badge-container">
            <img
              src={credential.imageUrl}
              alt={credential.title}
              className="badge-image"
            />
          </div>
        </div>

        {/* Right Panel */}
        <div className="credential-right-panel">
          <h1 className="credential-page-title">{t.pageTitle}</h1>

          <table className="credential-table">
            <tbody>
              <tr>
                <td className="label">{t.issuedTo}</td>
                <td className="value">{credential.customerName}</td>
              </tr>
              <tr>
                <td className="label">{t.consultantId}</td>
                <td className="value">{credential.customerId}</td>
              </tr>
              {credential.customerCountry && (
                <tr>
                  <td className="label">{t.location}</td>
                  <td className="value">{credential.customerCountry}</td>
                </tr>
              )}
              <tr>
                <td className="label">{t.title}</td>
                <td className="value">{credential.title}</td>
              </tr>
              <tr>
                <td className="label">{t.company}</td>
                <td className="value">Immunotec</td>
              </tr>
              <tr>
                <td className="label">{t.badgeId}</td>
                <td className="value">{credential.badgeId}</td>
              </tr>
              <tr>
                <td className="label">{t.issuedBy}</td>
                <td className="value">
                  <a
                    href="https://www.immunotec.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="immunotec-link"
                  >
                    Immunotec
                  </a>
                </td>
              </tr>
              <tr>
                <td className="label">{t.issueDate}</td>
                <td className="value">{formatDate(credential.issuedDate)}</td>
              </tr>

              <tr>
                <td className="label">{t.expirationDate}</td>
                <td className="value">
                  {credential.expirationDate
                    ? formatDate(credential.expirationDate)
                    : t.permanent}
                </td>
              </tr>
              <tr className="description-row">
                <td className="label">{t.description}</td>
                <td className="value description-text">{credential.content}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CredentialPage;
